<template>
	<call-draggable :data="employee" :type="CallDragTargetType.Employee" :draggable="true" :target="true" :targetTypes="[CallDragItemType.MyCallsCall, CallDragItemType.Employee, CallDragItemType.QueueCall]">
		<div ref="box" v-if="employee" class="box" @contextmenu="showContextMenu" @click="showContextMenuDelayed" @dblclick="callFirstExtension" :style="{'background':employee.presenceBackgroundColor}">

			<!-- Picture, state and birthdate flag -->
			<div class="shrink-0">
				<div style="position:relative">
					<PresenceIcon v-if="employee.type === PbxAppEmployeesType.EMPLOYEE" :state="employee.presence?.state as EmployeePresenceState" :size="17" class="presence-state" />
					<EmployeePicture :picture="employee.picture" :size="47" :spinner="true" :name="employee.name" />
					<img v-if="isBirthday" src="/images/birthdayflag.png" class="birthday" alt="Fødselsdagsflag">
				</div>
			</div>

			<!-- name, message, location, expire, calendar, call -->
			<div class="labels" style="flex-grow: 10;margin-top:1px">

				<div class="name">
					{{ employee.name }}
					<div v-if="employee.primaryExtension?.redirect" class="redirect">
						<div style="display:flex">
							<GlyphIcon :icon="GlyphIconName['forward-email']" size="14" color="#fff" style="margin-top:-2px" />
							<div style="margin-top:-3px">{{ ss.cutNationalPrefix(employee.primaryExtension.redirect, true) }}</div>
						</div>
					</div>
				</div>

				<!-- Calendar event-->
				<div class="message" v-if="hasCalendar">
					<GlyphIcon :icon="GlyphIconName['calendar']" size="14" color="var(--label-expires-color)" class="icon shrink-0" />
					<div v-if="employee.currentOrNextCalendarEvent" class="label flex gap-1 items-center">
						<div class="shrink-0"><b style="letter-spacing: -0.5px;">{{ employee.currentOrNextCalendarEvent.time ?? '' }}</b></div>
						<div>{{ employee.currentOrNextCalendarEvent.subject || t('calendarModal.private') }}</div>
						<div v-if="employee.currentOrNextCalendarEvent.restOfDay.length > 0" class="flex justify-center items-start shrink-0 calendar-rest-of-day">
							<i class="pi pi-plus" style="font-size:6px;margin-top:3px" />
							<div style="font-size:9px">{{ employee.currentOrNextCalendarEvent.restOfDay.length }}</div>
						</div>
					</div>
					<div v-if="!employee.currentOrNextCalendarEvent" class="label">{{ t('common.noEventsToday') }}</div>
				</div>

				<div class="message" v-if="employee.type === PbxAppEmployeesType.EMPLOYEE && inlineMessageAndLocation">
					<GlyphIcon :icon="GlyphIconName['chat-message']" size="13" class="shrink-0" />
					<div class="label shrink-0">{{ employee.presence?.message }}</div>
					<div class="shrink-0" style="width:10px"></div>
					<GlyphIcon :icon="GlyphIconName['map-marker']" size="13" color="var(--label-location-color)" class="shrink-0" />
					<div class="label" style="color:var(--label-location-color)">{{ employee.presence?.location }}</div>
				</div>

				<div class="message" v-if="employee.type === PbxAppEmployeesType.EMPLOYEE && !inlineMessageAndLocation && employee.presence?.message">
					<GlyphIcon :icon="GlyphIconName['chat-message']" size="13" class="shrink-0" />
					<div class="label">{{ employee.presence.message }}</div>
				</div>

				<div class="message" v-if="employee.type === PbxAppEmployeesType.EMPLOYEE && !inlineMessageAndLocation && employee.presence?.location">
					<GlyphIcon :icon="GlyphIconName['map-marker']" size="13" color="var(--label-location-color)" class="shrink-0" />
					<div class="label" style="color:var(--label-location-color)">{{ employee.presence.location }}</div>
				</div>

				<div class="expires" v-if="employee.type==='EMPLOYEE'" :class="{'visible':showExpires}">
					<GlyphIcon :icon="GlyphIconName['clock']" size="12" color="var(--label-expires-color)" class="icon" />
					<div class="label">{{ expiresLabel }}</div>
				</div>

				<div class="call" :class="{'visible':employee.showOtherParty}">
					<GlyphIcon :icon="employee.currentCallTypeIcon" size="13" color="var(--label-otherparty-color)" class="other-party-icon" />
					<div v-if="employee.currentCall && employee.currentCall.leg === 'I'" class="label">{{ ss.cutNationalPrefix(employee.currentCall.callee.number, true) }} {{ employee.currentCall.callee.name }}</div>
					<div v-if="employee.currentCall && employee.currentCall.leg === 'O' && !employee.currentCall.caller.secret" class="label">{{ employee.currentCall.caller.queueId ? employee.currentCall.caller.queueName + ': ' : '' }}{{ ss.cutNationalPrefix(employee.currentCall.caller.number, true) }} {{ employee.currentCall.caller.name }}</div>
					<div v-if="employee.currentCall && employee.currentCall.leg === 'O' && employee.currentCall.caller.secret" class="label">{{ employee.currentCall.caller.queueId ? employee.currentCall.caller.queueName + ': ' : '' }}{{ $t('employee.hiddenNumber') }}</div>
					<CountryFlag v-if="countryFlagName" :icon="countryFlagName" size="13" class="country-flag" />
				</div>
			</div>

			<!-- call duration box -->
			<div class="duration-box shrink-0" :class="{ 'visible': employee.currentCall }">
				<GlyphIcon :icon="employee.currentCallIcon" size="20" :color="employee.currentCallIconColor" style="margin: 0 auto;flex-shrink:0" :class="{ 'ringing': employee.currentCall && employee.currentCall.state === PbxCallState.CALL_RING, 'onhold': employee.currentCall && employee.currentCall.state === PbxCallState.CALL_HOLD}" />
				<div v-if="employee.currentCall && employee.currentCall.state !== PbxCallState.CALL_RING" class="duration-time">{{ duration(currentCallDuration, true, true) }}</div>
			</div>

			<ContextMenu ref="contextMenu" :model="contextMenuItems" popup @hide="contextMenuVisible = false" @show="contextMenuVisible = true" />
		</div>
		<div v-if="!last" class="bottom-line"></div>
	</call-draggable>
</template>

<script setup lang="ts">
import EmployeePicture from '@/components/EmployeePicture.vue'
import PresenceIcon from '@/components/PresenceIcon.vue'
import GlyphIcon from '@/components/GlyphIcon.vue'
import CountryFlag from '@/components/CountryFlag.vue'
import { useSettingsStore } from '@/stores/Settings'
import { useEmployeesStore } from '@/stores/Employees'
import globalEvents from '@/utilities/GlobalEvents'
import DesktopNotification from '@/utilities/DesktopNotification'
import CallDraggable from '@/components/dashboard/CallDraggable.vue'
import SipClient from '@/utilities/SipClient'
import { useI18n } from 'vue-i18n'
import logger from '@/utilities/Logger'
import { computed, onUnmounted, ref, watch } from 'vue'
import type Employee from '@/stores/Employee'
import { PbxAppEmployeesType, type PbxAppEmployeesExtensionCallWithDate, type PbxAppEmployeesExtensionWithDate } from '@/TcxApi/PbxApp'
import { formatDate, duration } from '@/utilities/filters'
import iso3166 from '@/utilities/iso3166'
import { EmployeePresenceState, type EmployeePresence } from '@/TcxApi/Employee'
import { useApiStore } from '@/stores/Api'
import { PbxCallState } from '@/TcxApi/PbxCommon'
import { openMailClient, sleep } from '@/utilities/Tools'
import type TieredMenu from 'primevue/tieredmenu'
import type { MenuItem } from 'primevue/menuitem'
import { CallDragItemType, CallDragTargetType } from '@/utilities/CallDrag'
import { GlyphIconName } from '@/stores/GlyphIcons.types'
import { hasGuestCall, isGuestCall } from '@/utilities/guestCallActions'

const ss = useSettingsStore()
const employees = useEmployeesStore()
const api = useApiStore()
const sipClient = SipClient.instance
const { t } = useI18n()
const contextMenu = ref<InstanceType<typeof TieredMenu>>()
const box = ref<HTMLDivElement>()
const contextMenuTimeout = ref<ReturnType<typeof setTimeout> | undefined>(undefined)
const contextMenuVisible = ref(false)
const contextMenuClosedAt = ref<number>(0)

interface Props {
	employeeId: string
	last: boolean
}

const props = defineProps<Props>()
const employee = ref<Employee | undefined>(undefined)
const currentCallDuration = ref(0)
const notifyWhenAvailable = ref(false)

const expiresLabel = computed(() => {
	if (employee.value?.type !== PbxAppEmployeesType.EMPLOYEE) { return '' }
	if (!employee.value.presence?.expire) { return undefined }

	let lbl = t(`common.presence.${employee.value.presence.state}`)
	const expire = new Date(employee.value.presence.expire)
	const inAWeek = new Date()
	const tomorrow = new Date()
	const today = new Date()

	inAWeek.setDate(inAWeek.getDate() + 7)
	inAWeek.setHours(0, 0, 0, 0)
	tomorrow.setDate(tomorrow.getDate() + 1)

	if (expire.getFullYear() === today.getFullYear() && expire.getMonth() === today.getMonth() && expire.getDate() === today.getDate()) {
		// kl. 18:00
		lbl += ` ${t('employee.until')} ${formatDate(expire, 'hh:mm')}`
	} else if (expire.getFullYear() === tomorrow.getFullYear() && expire.getMonth() === tomorrow.getMonth() && expire.getDate() === tomorrow.getDate()) {
		// imorgen kl. 18:00
		if (formatDate(expire, 'hh:mm') === '00:00') {
			lbl += ' ' + t('employee.restOfDay')
		} else {
			lbl += ' ' + t('employee.untilTomorrow') + ' ' + formatDate(expire, 'hh:mm')
		}
	} else if (expire < inAWeek) {
		lbl += ' ' + t('employee.until2') + ' ' + formatDate(expire, 'w')
		if (formatDate(expire, 'hh:mm') !== '00:00') {
			lbl += ' ' + t('common.at') + ' ' + formatDate(expire, 'hh:mm')
		}
	} else {
		lbl += ' ' + t('employee.until2') + ' ' + formatDate(expire, 'w')
		lbl += ' ' + t('employee.onThe') + ' ' + formatDate(expire, 'd/M')
		if (formatDate(expire, 'hh:mm') != '00:00') {
			lbl += ' ' + t('common.at') + ' ' + formatDate(expire, 'hh:mm')
		}
	}

	return lbl
})

const inlineMessageAndLocation = computed(() => {
	if (employee.value?.type !== PbxAppEmployeesType.EMPLOYEE || !employee.value.presence) { return false }

	const presence = employee.value.presence

	if (presence.message && presence.location && (presence.expire || employee.value.currentCall)) { return true }
	return false
})

const showExpires = computed(() => {
	if (employee.value?.type !== PbxAppEmployeesType.EMPLOYEE || !employee.value.presence) { return false }

	const presence = employee.value.presence

	if (!presence.expire) { return false }
	if (!employee.value.currentCall) { return true }
	if (employee.value.currentCall && !presence.message && !presence.location) { return true }
	return false
})

const countryFlagName = computed(() => {
	if (!employee.value?.currentCall) { return '' }

	let number = (employee.value.currentCall.leg === 'I' ? employee.value.currentCall.callee.number : employee.value.currentCall.caller.number).substring(0, 6)
	let iso: string | undefined = undefined

	while (!iso && number) {
		number = number.slice(0, -1)
		iso = iso3166.prefix[number]
	}
	if (iso) { iso = iso.toLowerCase() }
	if (number === ss.pbxSettings.nationalPrefix) { iso = undefined }
	return iso
})

const isBirthday = computed(() => {
	if (employee.value?.birthdate) {
		const bd = employee.value.birthdate
		const today = new Date()

		return bd.getMonth() === today.getMonth() && bd.getDate() === today.getDate()
	} else {
		return false
	}
})

const hasCalendar = computed(() => ss.auth?.customerIntegrations?.ms365?.enabled && employee.value?.type === PbxAppEmployeesType.EMPLOYEE)

watch(() => employee.value?.currentCall, (newValue, oldValue) => {
	if (newValue && !oldValue && contextMenuVisible.value) {
		contextMenu.value?.hide()
	}
})

watch(contextMenuVisible, newValue => {
	if (!newValue) { contextMenuClosedAt.value = new Date().getTime() }
})

function callFirstExtension() { // A.k.a. double click
	if (contextMenuTimeout.value) {
		clearTimeout(contextMenuTimeout.value)
		contextMenuTimeout.value = undefined
	}

	let ext = employee.value?.extensions.find(o => o.primary === true)
	if (!ext) { ext = employee.value?.extensions[0] }
	if (ext) {
		logger.info(`Employee: User is calling employee ${employee.value?.name}`)
		call(ext)
	}
}

function showContextMenu(event: MouseEvent) {
	event.preventDefault()
	if (contextMenuVisible.value) {
		contextMenu.value?.hide()
	} else {
		globalEvents.emit('HideContextMenu')
		contextMenu.value?.show(event)
	}
}

function showContextMenuDelayed(event: MouseEvent) {
	event.preventDefault()
	if (new Date().getTime() - contextMenuClosedAt.value < 100) {
		return
	}
	contextMenuTimeout.value = setTimeout(() => {
		if (contextMenuTimeout.value) {
			contextMenuTimeout.value = undefined
			if (!contextMenuVisible.value) {
				globalEvents.emit('HideContextMenu')
				contextMenu.value?.show(event)
			}
		}
	}, 200)
}

function hideContextMenu() {
	contextMenu.value?.hide()
}

const contextMenuItems = computed(() => {
	if (!employees.me || !employee.value) { return [] }

	const menu: MenuItem[] = []
	const perms = employee.value.permissions
	const iHaveGuestCall = hasGuestCall(employees.myCalls)
	let myCalls = employees.myCalls.filter(call => !isGuestCall(call))
	const myCallsSessionIds = myCalls.map(o => o.sessionId)
	const cc = employee.value.currentCall
	const cr = employee.value.currentRingingCall
	const ccIsWithMe = cc && myCallsSessionIds.includes(cc.sessionId)
	const crIsWithMe = cr && myCallsSessionIds.includes(cr.sessionId)
	if (ccIsWithMe) {
		myCalls = myCalls.filter(o => o.sessionId !== cc.sessionId)
	}
	if (crIsWithMe) {
		myCalls = myCalls.filter(o => o.sessionId !== cr.sessionId)
	}

	if (employee.value._id !== ss.auth?.employee) {
		// Dial
		if (employee.value.extensions.length > 1) {
			const submenu: MenuItem[] = []

			for (const ext of employee.value.extensions) {
				const label = ext.number ?? ext.direct ?? (ext.mobile && ext.mobile.length > 0 ? ext.mobile[0] : undefined)
				if (label) {
					submenu.push({ label: ss.cutNationalPrefix(label), command: () => { call(ext) } })
				}
			}
			menu.push({ label: t('employee.call'), icon: 'pi pi-phone', items: submenu })
		} else if (employee.value.extensions.length === 1) {
			const ext = employee.value.extensions[0] as PbxAppEmployeesExtensionWithDate

			menu.push({ label: t('employee.call'), icon: 'pi pi-phone', command: () => { call(ext) } })
		}

		// Transfer
		if (myCalls.length > 0 && employee.value.presence?.state !== EmployeePresenceState.BUSY) {
			if (myCalls.length > 1 && (!cc || ccIsWithMe)) {
				const submenu: MenuItem[] = []

				for (const call of myCalls) {
					if (!cc || call.sessionId !== cc.sessionId) {
						const label = call.leg === 'I' ? ss.cutNationalPrefix(call.callee.number) + ' ' + (call.callee.name || '') : ss.cutNationalPrefix(call.caller.number) + ' ' + (call.caller.name || '')

						submenu.push({
							label: label,
							icon: 'pi pi-phone',
							command: () => { transfer(call, cc, ccIsWithMe) } // ccIsWithMe = attended transfer to cc ELSE blind transfer
						})
					}
				}
				menu.push({
					label: t('employee.transfer') + (cc && !ccIsWithMe ? ' ' + t('employee.toPersonalQueue') : ' ' + t('employee.toHere')),
					icon: 'pi pi-arrow-right',
					items: submenu
				})
			} else {
				menu.push({
					label: t('employee.transfer') + (cc && !ccIsWithMe ? ' ' + t('employee.toPersonalQueue') : ' ' + t('employee.toHere')),
					icon: 'pi pi-arrow-right',
					command: () => { transfer(myCalls[0] as PbxAppEmployeesExtensionCallWithDate, cc, ccIsWithMe) }
				}) // ccIsWithMe = attended transfer to cc ELSE blind transfer
			}
		}

		// Add in
		if (!iHaveGuestCall && myCalls.length > 0 && myCalls.some(call => call.state === PbxCallState.CALL_ANSWER)) {
			menu.push({ label: t('employee.addToCall'), icon: 'pi pi-plus', command: () => { addCall(myCalls.find(call => call.state === PbxCallState.CALL_ANSWER) as PbxAppEmployeesExtensionCallWithDate) } })
		}

		if (cc || (cr && perms.pickup) || (cc && perms.steal) || (cc && perms.spy)) {
			menu.push({ separator: true })
		}

		// Notify when available
		if (cc || (employee.value.type === PbxAppEmployeesType.EMPLOYEE && employee.value.presence?.state === EmployeePresenceState.BUSY)) {
			menu.push({
				label: t('employee.notifyWhenAvailable'),
				icon: notifyWhenAvailable.value ? 'pi pi-check-circle' : 'pi pi-circle',
				command: () => { notify(!notifyWhenAvailable.value) } })
		}

		// Pickup ringing call
		if (cr && perms.pickup && !isGuestCall(cr)) { menu.push({ label: t('employee.pickupRingingCall'), icon: 'pi pi-arrow-up', command: () => { pickup(cr) } }) }
		if (cc && cc.state == 'CALL_ANSWER' && perms.steal && !isGuestCall(cc)) { menu.push({ label: t('employee.pickupCurrentCall'), icon: 'pi pi-arrow-up-left', command: () => { steal(cc) } }) }
		if (cc && cc.state == 'CALL_ANSWER' && perms.spy && !isGuestCall(cc)) { menu.push({ label: t('employee.listenIn'), icon: 'pi pi-volume-up', command: () => { spy(cc) } }) }

		menu.push({ separator: true })

		const mobiles: string[] = []

		for (const ext of employee.value.extensions) {
			if (ext.mobile) { mobiles.push(...ext.mobile) }
		}

		// Calendar
		if (hasCalendar.value) {
			menu.push({ label: t('mypresence.action.calendar'), icon: 'pi pi-calendar', command: () => showCalendar(), visible: hasCalendar.value })
		}

		// Send SMS
		if (mobiles.length === 1) {
			menu.push({ label: t('employee.sendSms'), icon: 'pi pi-comment', command: () => { sendSms(mobiles[0] as string) } })
		} else if (mobiles.length > 1) {
			const submenu: MenuItem[] = []

			for (const mobile of mobiles) {
				submenu.push({ label: ss.cutNationalPrefix(mobile), icon: 'pi pi-comment', command: () => { sendSms(mobile) } })
			}
			menu.push({ label: t('employee.sendSms'), icon: 'pi pi-comment', items: submenu })
		}

		// Send email
		if (employee.value.emailAddress && employee.value.emailAddress.includes('@') && !employee.value.emailAddress.includes('@xtele.dk')) {
			menu.push({ label: t('employee.sendEmail'), icon: 'pi pi-envelope', command: () => { sendEmail() } })
		}

		// Copy number
		if (employee.value.currentCall && employee.value.currentCall.leg === 'I') {
			const number = ss.cutNationalPrefix(employee.value.currentCall.callee.number)

			menu.push({ label: t('employee.copyNumberToClipboard'), icon: 'pi pi-copy', command: () => { navigator.clipboard.writeText(number) } })
		} else if (employee.value.currentCall && employee.value.currentCall.leg === 'O' && !employee.value.currentCall.caller.secret) {
			const number = ss.cutNationalPrefix(employee.value.currentCall.caller.number)

			menu.push({ label: t('employee.copyNumberToClipboard'), icon: 'pi pi-copy', command: () => { navigator.clipboard.writeText(number) } })
		}

		// Set presence
		if (employee.value.type === PbxAppEmployeesType.EMPLOYEE && perms.setPresence) {
			menu.push({ label: t('employee.setPresence'), icon: 'pi pi-user', command: () => { showSetPresence() } })
			menu.push({ label: t('employee.quickSetPresence'), icon: 'pi pi-user-plus',
				items: [
					{ label: t('common.presence.AVAILABLE'), icon: 'pi pi-circle-fill presence-available-color', command: () => { setStatus(EmployeePresenceState.AVAILABLE) } },
					{ label: t('common.presence.BUSY'), icon: 'pi pi-circle-fill presence-busy-color', command: () => { setStatus(EmployeePresenceState.BUSY) } },
					{ label: t('common.presence.OUT_OF_OFFICE'), icon: 'pi pi-circle-fill presence-out-of-office-color', command: () => { setStatus(EmployeePresenceState.OUT_OF_OFFICE) } },
					{ label: t('common.presence.OFF_WORK'), icon: 'pi pi-circle-fill presence-off-work-color', command: () => { setStatus(EmployeePresenceState.OFF_WORK) } },
					{ label: t('common.presence.HOLIDAY'), icon: 'pi pi-circle-fill presence-holiday-color', command: () => { setStatus(EmployeePresenceState.HOLIDAY) } }
				]
			})
			menu.push({ label: t('employee.redirect'), icon: 'pi pi-arrow-right', command: () => { showRedirect() } })
		}
	}

	if (employee.value.type === PbxAppEmployeesType.EMPLOYEE) {
		menu.push({ label: t('employee.showInfo'), icon: 'pi pi-info-circle', command: () => { showInfo() } })
	}

	return menu
})

function showSetPresence() {
	globalEvents.emit('CommonSetPresenceModal', employee.value as Employee)
}

function showCalendar() {
	if (employee.value) {
		globalEvents.emit('CommonCalendarModal', employee.value)
	}
}

function showRedirect() {
	if (!employee.value) { return }
	globalEvents.emit('CommonSetRedirectModal', employee.value)
}

function refreshDurationAndCheckNotify() {
	currentCallDuration.value = employee.value && employee.value.currentCall ? employee.value.currentCallDuration : 0

	if (notifyWhenAvailable.value && !employee.value?.currentCall && employee.value?.presence?.state !== EmployeePresenceState.BUSY) {
		showAvailableNotification()
	}
}

function showAvailableNotification() {
	if (!employee.value) { return }
	logger.info(`Employee: ${employee.value.name} is now available - showing notification`)

	notifyWhenAvailable.value = false
	const icon = employee.value.picture ? `https://image.telecomx.dk/${employee.value.picture}.png?width=256&height=256&token=${ss.token}` : undefined

	DesktopNotification.show(`${employee.value.name} ${t('employee.notifyAvailableHeader')}`, t('employee.notifyAvailableMessage'), icon, false, () => {
		call(employee.value?.primaryExtension as PbxAppEmployeesExtensionWithDate)
	})
}

function noPhoneSelected() {
	globalEvents.emit('CommonErrorModal', { header: t('common.error.noPhoneHeader'), message: t('common.error.noPhoneMessage') })
}

async function call(extension: PbxAppEmployeesExtensionWithDate) {
	// call this extension
	if (!ss.realPhoneId) { noPhoneSelected(); return }

	if (ss.settings.softphoneId) {
		let number = extension.number
		if (!number && extension.direct) { number = ss.cutNationalPrefix(extension.direct) }
		if (!number && extension.mobile && extension.mobile.length > 0) { number = ss.cutNationalPrefix(extension.mobile[0] as string) }
		if (!number) { number = extension._id }
		logger.info(`Employee: User calling employee ${employee.value?.name} at ${number}`)

		globalEvents.emit('Call:ActivateHold') // In case the user is on a call
		await sleep(100)
		sipClient.call(number)
	} else {
		logger.info(`Employee: User calling employee ${employee.value?.name}`)

		try { await api.get(`/pbx/app/action/call?device=${ss.realPhoneId}&target=${extension._id}`) }
		catch (_) {
			globalEvents.emit('CommonErrorModal', { header: t('common.error.callFailedHeader'), message: t('common.error.callFailedMessage') })
		}
	}
}

async function transfer(myCall: PbxAppEmployeesExtensionCallWithDate, employeeCall?: PbxAppEmployeesExtensionCallWithDate, callIsWithMe = false) { // _id, customer, extension, state, started, answered, caller{number,name,secret}, callee{number,name}, sessionId, channel, calId, direction, leg, originator, device, pbx
	if (!employee.value) { return }

	if (callIsWithMe && employeeCall?.state === PbxCallState.CALL_ANSWER) {
		// Attended transfer
		logger.info(`Employee: User performs attended transfer of ${(myCall.leg === 'I' ? myCall.callee.number : myCall.caller.number)} to ${employee.value.name}`)
		globalEvents.emit('AttendedTransferRequested', { source: myCall._id, destination: employeeCall._id })

		try { await api.get(`/pbx/app/action/transfer?source=${myCall._id}&destination=${employeeCall._id}`) }
		catch (_) {
			globalEvents.emit('CommonErrorModal', { header: t('common.error.transferFailedHeader'), message: t('common.error.transferFailedMessage') })
		}
	} else if (callIsWithMe && employeeCall?.state === PbxCallState.CALL_RING) {
		logger.info(`Employee: User performs transfer of ${(myCall.leg === 'I' ? myCall.callee.number : myCall.caller.number)} to ${employee.value.name}`)

		try {
			// Hangup my call first
			await api.get(`/pbx/app/action/hangup?target=${employeeCall._id}`)
			globalEvents.emit('BlindTransferRequested', { source: myCall._id, target: employee.value.primaryExtension?._id as string })
			await api.get(`/pbx/app/action/transfer?source=${myCall._id}&target=${employee.value.primaryExtension?._id as string}`)
		}
		catch (_) {
			globalEvents.emit('CommonErrorModal', { header: t('common.error.transferFailedHeader'), message: t('common.error.transferFailedMessage') })
		}
	} else {
		// Blind transfer
		logger.info(`Employee: User performs blind transfer of ${(myCall.leg === 'I' ? myCall.callee.number : myCall.caller.number)} to ${employee.value.name}`)
		globalEvents.emit('BlindTransferRequested', { source: myCall._id, target: employee.value.primaryExtension?._id as string })

		try { await api.get(`/pbx/app/action/transfer?source=${myCall._id}&target=${employee.value.primaryExtension?._id as string}`) }
		catch (_) {
			globalEvents.emit('CommonErrorModal', { header: t('common.error.transferFailedHeader'), message: t('common.error.transferFailedMessage') })
		}
	}
}

async function addCall(call: PbxAppEmployeesExtensionCallWithDate) {
	// add a new call to this extension on existing bridge where 'call' is connected
	logger.info(`Employee: User is adding ${employee.value?.name} to the users existing call with ${(call.leg === 'I' ? call.callee.number : call.caller.number)}`)

	try { await api.get(`/pbx/app/action/invite?source=${call._id}&target=${employee.value?.primaryExtension?._id as string}`) }
	catch (_) {
		globalEvents.emit('CommonErrorModal', { header: t('common.error.inviteFailedHeader'), message: t('common.error.inviteFailedMessage').replace('%%number%%', employee.value?.name as string) })
	}
}

function notify(enable: boolean) {
	logger.info(`Employee: User ${enable ? 'enabled' : 'disabled'} notification for when the employee ${employee.value?.name} is available`)
	notifyWhenAvailable.value = enable
}

async function pickup(call: PbxAppEmployeesExtensionCallWithDate) {
	// pickup this call
	logger.info(`Employee: User is picking up a ringing phone from ${employee.value?.name} with caller ${(call.leg === 'I' ? call.callee.number : call.caller.number)}`)
	if (!ss.realPhoneId) { noPhoneSelected(); return }

	globalEvents.emit('MyCallsAutoAnswerNextRinging', call.sessionId)

	try { await api.get(`/pbx/app/action/pickup?device=${ss.realPhoneId}&target=${call._id}`) }
	catch (_) {
		globalEvents.emit('CommonErrorModal', { header: t('common.error.pickFailedHeader'), message: t('common.error.pickupFailedMessage') })
	}
}

async function steal(call: PbxAppEmployeesExtensionCallWithDate) {
	// steal this call
	logger.info(`Employee: User is stealing an ongoing call from ${employee.value?.name} who is talking to ${(call.leg === 'O' ? call.caller.number : call.callee.number)}`)
	if (!ss.realPhoneId) { noPhoneSelected(); return }

	globalEvents.emit('MyCallsAutoAnswerNextRinging', call.sessionId)

	try { await api.get(`/pbx/app/action/steal?device=${ss.realPhoneId}&target=${call._id}`) }
	catch (_) {
		globalEvents.emit('CommonErrorModal', { header: t('common.error.stealFailedHeader'), message: t('common.error.stealFailedMessage') })
	}
}

async function spy(call: PbxAppEmployeesExtensionCallWithDate) {
	// spy on this call
	logger.info(`Employee: User is spying on ${employee.value?.name} who is talking to ${(call.leg === 'O' ? call.caller.number : call.callee.number)}`)
	if (!ss.realPhoneId) { noPhoneSelected(); return }

	globalEvents.emit('MyCallsAutoAnswerNextRinging', call.sessionId)

	try { await api.get(`/pbx/app/action/spy?device=${ss.realPhoneId}&target=${call._id}`) }
	catch (_) {
		globalEvents.emit('CommonErrorModal', { header: t('common.error.spyFailedHeader'), message: t('common.error.spyFailedMessage') })
	}
}

function sendSms(number: string) {
	logger.info(`Employee: User is starting to write an SMS for ${employee.value?.name} at ${number}`)
	globalEvents.emit('CommonSendSmsModal', { employee: employee.value, number: number })
}

function sendEmail() {
	const call = employees.me?.currentCall
	let message = ''

	if (call) {
		const caller = call.leg === 'O' ? call.caller : call.callee
		if (caller.name) {
			message = t('employee.emailMessageWithName').replace('%%name%%', caller.name).replace('%%number%%', ss.cutNationalPrefix(caller.number))
		} else {
			message = t('employee.emailMessage').replace('%%number%%', ss.cutNationalPrefix(caller.number))
		}
	}

	logger.info(`Employee: User is writing an e-mail to ${employee.value?.name} at ${employee.value?.emailAddress}`)
	if (employee.value?.emailAddress) {
		sipClient.ignoreUnloadEvent = true
		if (message) {
			openMailClient(employee.value.emailAddress, t('employee.emailSubject'), message)
		} else {
			openMailClient(employee.value.emailAddress)
		}
	}
}

async function setStatus(state: EmployeePresenceState) {
	if (!employee.value) { return }
	logger.info(`Employee: User is setting status for ${employee.value.name} to ${state}`)

	const presence: Partial<EmployeePresence> = { state: state } // , expire: null, message: '', location: '' } };

	if (state === EmployeePresenceState.AVAILABLE) {
		presence.expire = null
		presence.message = ''
		presence.location = ''
		presence.overrides = []
	}

	try {
		await api.post(`/employee/${employee.value._id}`, { presence: presence })
	}
	catch (_) {
		globalEvents.emit('CommonErrorModal', { header: t('common.error.setPresenceFailedHeader'), message: t('common.error.setPresenceFailedMessage').replace('%%name%%', employee.value.name) })
	}
}

function showInfo() {
	// console.log('Show info for ' + this.employee.name);
	globalEvents.emit('CommonEmployeeDataModal', employee.value as Employee)
}

employee.value = employees.getEmployee(props.employeeId)
globalEvents.on('OneSecond', refreshDurationAndCheckNotify)
globalEvents.on('HideContextMenu', hideContextMenu)
setTimeout(() => { refreshDurationAndCheckNotify() }, 100)

if (notifyWhenAvailable.value && !employee.value?.currentCall) {
	notifyWhenAvailable.value = false
}

onUnmounted(() => {
	globalEvents.off('OneSecond', refreshDurationAndCheckNotify)
	globalEvents.off('HideContextMenu', hideContextMenu)
})
</script>

<style scoped>
	.box {
		cursor: context-menu;
		margin: 1px 0 1px 4px;
		height: 57px;
		display: flex;
		flex-direction: row;
		align-items: center;
		border-top-left-radius: 30px;
		border-bottom-left-radius: 30px;
	}
	.box:hover {
		background: #00000010;
	}
	@media(prefers-color-scheme: dark) {
		.box:hover {
			background: #ffffff10;
		}
	}
	.presence-state {
		position: absolute;
		left: 33px;
		bottom: 1px;
		z-index: 1;
	}
	.labels {
		padding-left: 6px;
		display: flex;
		flex-direction: column;
		justify-content: center;
		margin-bottom: 2px;
		margin-right: 2px;
		font-size: 13px;
		line-height: 13px;
		min-width: 0;
		color: var(--p-text-muted-color);
		/* background: lightblue; */
	}
	/* Clip sideways only, for the ellipsis: the line-heights are tighter than Inter's accents (the ring on Å)
	   and descenders, so clipping vertically too shaved the tops and tails off the letters */
	.labels DIV {
		overflow: clip visible;
		min-width: 0; /* overflow:hidden let flex items shrink below their text, clip does not */
		min-height: 0;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	/* These do need the vertical clip: .expires and .call collapse to height 0, the redirect pill is shorter than its line */
	.labels .expires, .labels .call, .labels .redirect {
		overflow: hidden;
	}
	.name {
		font-size: 14px;
		line-height: 16px;
		font-weight:500;
		letter-spacing: 0.01em;
		color: var(--p-text-color);
	}
	.message {
		font-size: 11px;
		line-height: 12px;
		display: inline-flex;
		letter-spacing: 0.01em;
	}
	.expires {
		font-size: 11px;
		font-style: italic;
		color: var(--label-expires-color);
		line-height: 12px;
		display: inline-flex;
		height: 0px;
		transition: 400ms;
	}
	/* Once shown, the box starts 2px above the text (same layout: 15px + 2px padding - 2px margin) so an Å is not clipped */
	.expires.visible, .call.visible {
		height: 17px;
		padding-top: 2px;
		margin-top: -2px;
	}
	.call {
		font-size: 11px;
		font-style: italic;
		color: var(--label-otherparty-color);
		line-height: 12px;
		display: inline-flex;
		height: 0px;
		transition: 400ms;
	}
	.label {
		padding-left: 2px;
	}
	/* Inter has no italic face, so the browser slants the upright glyphs and the last one
	   overhangs its box; leave room for it or the ellipsis clip cuts it (the "d" in "Kildelund") */
	.call .label, .expires .label {
		padding-right: 0.25em;
	}
	.other-party-icon {
		margin-top: -1px;
	}
	.duration-box {
		height: 35px;
		display: flex;
		flex-direction: column;
		justify-content: center;
		float: right;
		transition: 600ms;
		transition-delay: 300ms;
		max-width: 0px;
		min-width: 0px;
		overflow: hidden;
		margin-top: 5px;
	}
	.duration-box.visible {
		max-width: 65px;
		min-width: 40px;
	}
	.duration-time {
		color: var(--p-text-muted-color);
		font-family: RobotoMonoThin;
		font-size: 11px;
		flex-shrink: 0;
		margin: 0 auto;
	}
	.ringing {
		animation: shake 1s;
		animation-iteration-count: infinite;
	}

	@keyframes shake {
		0% { transform: translate(2px, 0px); }
		10% { transform: translate(-2px, 1px); }
		20% { transform: translate(2px, -1px); }
		30% { transform: translate(-2px, 0px); }
		40% { transform: translate(2px, 0px); }
		50% { transform: translate(-2px, 0px); }
		60% { transform: translate(2px, 0px); }
		70% { transform: translate(-2px, 1px); }
		80% { transform: translate(2px, -1px); }
		90% { transform: translate(-2px, 0px); }
		100% { transform: translate(2px, 0px); }
	}
	.onhold {
		animation: holdanim 1s;
		animation-iteration-count: infinite;
	}
	@keyframes holdanim {
		0% { opacity: 1; }
		40% { opacity: 0.2; }
		70% { opacity: 1; }
	}
	.birthday {
		width: 24px;
		position: absolute;
		bottom: -2px;
	}
	.bottom-line {
		margin-left: 65px; /** 52 */
		border-bottom: 1px solid var(--p-form-field-border-color);
		opacity: 0.5;
	}
	.country-flag {
		margin-left: 3px;
		margin-top: 1px;
	}
	.redirect {
		font-size: 11px;
		font-weight: 400;
		background: var(--bg-color-dashboard-header);
		padding: 1px 2px;
		display: inline-block;
		color: #fff;
		height: 13px;
		border-radius: 3px;
	}
	.calendar-rest-of-day {
		color: var(--p-primary-contrast-color);
		background: var(--p-surface-400);
		border-radius: 4px;
		padding: 0px 3px;
		margin-right: 2px;
		font-size: 8px;
	}

</style>
